import React, { useState } from "react";
import {
  Type,
  FileText,
  List,
  CheckSquare,
  Circle,
  Sliders,
  Palette,
  Link,
  BarChart3,
  Edit3,
  Copy,
  Trash2,
  GripVertical,
  Settings,
  Eye,
  EyeOff,
} from "lucide-react";
import { QUESTION_TYPES } from "../models/types";
import InlineQuestionEditor from "./InlineQuestionEditor";

const QuestionCard = ({
  question,
  onEdit,
  onDelete,
  onDuplicate,
  onUpdate,
  dragHandleProps,
  isEditing = false,
  isPreview = false,
}) => {
  const [showPreview, setShowPreview] = useState(isPreview);
  const [isInlineEditing, setIsInlineEditing] = useState(isEditing);

  const getQuestionIcon = (type) => {
    const iconMap = {
      [QUESTION_TYPES.TEXT]: Type,
      [QUESTION_TYPES.TEXTAREA]: FileText,
      [QUESTION_TYPES.SELECT]: List,
      [QUESTION_TYPES.RADIO]: Circle,
      [QUESTION_TYPES.CHECKBOX]: CheckSquare,
      [QUESTION_TYPES.SCALE_1_10]: BarChart3,
      [QUESTION_TYPES.SLIDER]: Sliders,
      [QUESTION_TYPES.COLOR]: Palette,
      [QUESTION_TYPES.URL]: Link,
    };
    return iconMap[type] || Type;
  };

  const getQuestionTypeLabel = (type) => {
    const labelMap = {
      [QUESTION_TYPES.TEXT]: "Short Answer",
      [QUESTION_TYPES.TEXTAREA]: "Paragraph",
      [QUESTION_TYPES.SELECT]: "Dropdown",
      [QUESTION_TYPES.MULTISELECT]: "Multi-Select",
      [QUESTION_TYPES.RADIO]: "Multiple Choice",
      [QUESTION_TYPES.CHECKBOX]: "Checkboxes",
      [QUESTION_TYPES.SCALE_1_10]: "Linear Scale",
      [QUESTION_TYPES.SLIDER]: "Slider",
      [QUESTION_TYPES.COLOR]: "Color Picker",
      [QUESTION_TYPES.URL]: "Website URL",
    };
    return labelMap[type] || "Unknown";
  };

  const renderQuestionPreview = () => {
    const Icon = getQuestionIcon(question.type);

    return (
      <div className="bg-neutral-800/60 backdrop-blur-sm border border-neutral-700 rounded-xl p-6 hover:shadow-lg transition-shadow">
        <div className="space-y-4">
          {/* Question Header */}
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <Icon size={20} className="text-neutral-400" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-white">
                  {question.title}
                  {question.required && (
                    <span className="text-primary-green ml-1">*</span>
                  )}
                </h3>
                {question.description && (
                  <p className="text-sm text-neutral-400 mt-1">
                    {question.description}
                  </p>
                )}
                <p className="text-xs text-neutral-500 mt-1">
                  {getQuestionTypeLabel(question.type)}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-1">
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="p-1.5 text-neutral-400 hover:text-white hover:bg-neutral-700 rounded"
                title="Toggle Preview"
              >
                {showPreview ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
              <button
                onClick={() => setIsInlineEditing(true)}
                className="p-1.5 text-neutral-400 hover:text-white hover:bg-neutral-700 rounded"
                title="Edit Question"
              >
                <Edit3 size={16} />
              </button>
              {onDuplicate && (
                <button
                  onClick={() => onDuplicate(question)}
                  className="p-1.5 text-neutral-400 hover:text-primary-green hover:bg-primary-green/10 rounded"
                  title="Duplicate Question"
                >
                  <Copy size={16} />
                </button>
              )}
              {onDelete && (
                <button
                  onClick={() => onDelete(question.id)}
                  className="p-1.5 text-neutral-400 hover:text-red-400 hover:bg-red-500/10 rounded"
                  title="Delete Question"
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          </div>

          {/* Question Input Preview */}
          <div className="mt-2">
            {question.type === QUESTION_TYPES.TEXT && (
              <input
                type="text"
                placeholder={question.placeholder || "Your answer"}
                className="w-full px-4 py-3 bg-neutral-800 border border-neutral-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green text-white"
                disabled
              />
            )}

            {question.type === QUESTION_TYPES.TEXTAREA && (
              <textarea
                placeholder={question.placeholder || "Your answer"}
                className="w-full px-4 py-3 bg-neutral-800 border border-neutral-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green text-white resize-none h-24"
                disabled
              />
            )}

            {question.type === QUESTION_TYPES.SELECT && (
              <select
                className="w-full px-4 py-3 bg-neutral-800 border border-neutral-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green text-white appearance-none"
                disabled
              >
                <option>Select an option</option>
                {question.options?.map((option, index) => (
                  <option key={index} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            )}

            {question.type === QUESTION_TYPES.MULTISELECT && (
              <select
                multiple
                className="w-full px-4 py-3 bg-neutral-800 border border-neutral-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green text-white appearance-none h-24"
                disabled
              >
                {question.options?.map((option, index) => (
                  <option key={index} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            )}

            {question.type === QUESTION_TYPES.RADIO && (
              <div className="space-y-3">
                {question.options?.map((option, index) => (
                  <label key={index} className="flex items-center">
                    <input
                      type="radio"
                      name={`preview-${question.id}`}
                      className="mr-3 text-primary-green focus:ring-primary-green"
                      disabled
                    />
                    <span className="text-white">{option.label}</span>
                  </label>
                ))}
              </div>
            )}

            {question.type === QUESTION_TYPES.CHECKBOX && (
              <div className="space-y-3">
                {question.options?.map((option, index) => (
                  <label key={index} className="flex items-center">
                    <input
                      type="checkbox"
                      className="mr-3 text-primary-green focus:ring-primary-green"
                      disabled
                    />
                    <span className="text-white">{option.label}</span>
                  </label>
                ))}
              </div>
            )}

            {question.type === QUESTION_TYPES.SCALE_1_10 && (
              <div className="space-y-4">
                <div className="flex justify-between text-sm text-neutral-400">
                  <span>{question.leftLabel || "Low"}</span>
                  <span>{question.rightLabel || "High"}</span>
                </div>
                <div className="flex space-x-3">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                    <label key={num} className="flex items-center">
                      <input
                        type="radio"
                        name={`scale-${question.id}`}
                        className="mr-2 text-primary-green focus:ring-primary-green"
                        disabled
                      />
                      <span className="text-sm text-white">{num}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {question.type === QUESTION_TYPES.SLIDER && (
              <div className="space-y-3">
                <input
                  type="range"
                  min="0"
                  max="100"
                  className="w-full accent-primary-green"
                  disabled
                />
                <div className="flex justify-between text-sm text-neutral-400">
                  <span>0</span>
                  <span>100</span>
                </div>
              </div>
            )}

            {question.type === QUESTION_TYPES.COLOR && (
              <input
                type="color"
                className="w-16 h-12 border border-neutral-700 rounded-lg bg-neutral-800"
                disabled
              />
            )}

            {question.type === QUESTION_TYPES.URL && (
              <input
                type="url"
                placeholder={question.placeholder || "https://example.com"}
                className="w-full px-4 py-3 bg-neutral-800 border border-neutral-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green text-white"
                disabled
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderDragHandle = () => (
    <div
      {...dragHandleProps}
      className="absolute left-0 top-0 bottom-0 w-6 flex items-center justify-center cursor-grab active:cursor-grabbing bg-gray-50 hover:bg-gray-100 rounded-l-lg"
    >
      <GripVertical size={16} className="text-gray-400" />
    </div>
  );

  if (isInlineEditing) {
    return (
      <div className="relative">
        {dragHandleProps && renderDragHandle()}
        <div className={dragHandleProps ? "ml-6" : ""}>
          <InlineQuestionEditor
            question={question}
            onSave={(updatedQuestion) => {
              onUpdate && onUpdate(updatedQuestion);
              setIsInlineEditing(false);
            }}
            onCancel={() => setIsInlineEditing(false)}
            onDuplicate={onDuplicate}
            onDelete={onDelete}
            isPreview={showPreview}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {dragHandleProps && renderDragHandle()}
      <div className={dragHandleProps ? "ml-6" : ""}>
        {renderQuestionPreview()}
      </div>
    </div>
  );
};

export default QuestionCard;
